import { Params, SettingsPayload } from '../types';
import { PreferenceClient } from '../client/preference.client';
import { deepEqual } from '../tools/object';
import { PageInfo, PageSettings } from './types';

const PAGE_KEY = '__page__';
const PAGE_SETTINGS_PRE_CACHE = 'settings_pre_cache';
const CACHE_SUFFIX = '_page_values';

export class PageSettingsManager {
  private static _instance: PageSettingsManager;
  private pages: Record<string, PageSettings> = {};
  private active: string;
  private prevPage: string;

  private constructor() {}

  async registerPage(name: string, syncSettings: PageInfo['syncSettings']): Promise<void> {
    this.prevPage = this.active;

    if (!this.pages[name]) {
      const cached = await PreferenceClient.getInstance().retrieve(`${name}${CACHE_SUFFIX}`);
      if (cached) {
        this.pages[name] = name === 'segments' ? this.validateAndClearInvalidCustomFeatures(cached) : cached;
      } else {
        const initValue = {
          [PAGE_KEY]: {},
        };
        await PreferenceClient.getInstance().store(`${name}${CACHE_SUFFIX}`, initValue);
        this.pages[name] = initValue;
      }
    }
    this.active = name;

    await this.syncPageSettings(name, syncSettings);

    const preCached = this.getPreCachedSettings();
    if (preCached) {
      const storedSettings = this.pages[name];
      const combinedSettings: PageSettings = {};
      Object.keys(storedSettings).forEach(key => {
        const stored = storedSettings[key];
        const cached = preCached[key] || {};
        combinedSettings[key] = {
          ...{},
          ...stored,
          ...cached,
        };
      });

      Object.keys(preCached).forEach(key => {
        if (!combinedSettings[key]) {
          combinedSettings[key] = preCached[key];
        }
      });

      this.pages[name] = combinedSettings;
      PreferenceClient.getInstance().store(`${name}${CACHE_SUFFIX}`, combinedSettings);
    }
  }

  async syncPageSettings(name: string, syncSettings: PageInfo['syncSettings']): Promise<void> {
    if (!syncSettings || !this.prevPage || this.prevPage !== syncSettings.fromPage) {
      return;
    }
    const pageSettings = this.pages[name];
    const { fromPage, components } = syncSettings;

    const fromPageSettings =
      this.pages[fromPage] || (await PreferenceClient.getInstance().retrieve(`${fromPage}${CACHE_SUFFIX}`));
    if (!fromPageSettings) {
      return;
    }

    let settingsChanged = false;

    components.forEach(({ from: fromComponent, to: toComponent }) => {
      const fromComponentSettings = fromPageSettings[fromComponent.name];
      const toComponentSettings = pageSettings[toComponent.name];

      if (!fromComponentSettings || !toComponentSettings) return;

      fromComponent.settings.forEach((setting, index) => {
        if (!deepEqual(fromComponentSettings[setting], toComponentSettings[toComponent.settings[index]])) {
          toComponentSettings[toComponent.settings[index]] = fromComponentSettings[setting];
          settingsChanged = true;
        }
      });

      pageSettings[toComponent.name] = toComponentSettings;
    });

    if (settingsChanged) {
      this.pages[name] = pageSettings;
      await PreferenceClient.getInstance().store(`${name}${CACHE_SUFFIX}`, pageSettings);
    }
  }

  /**
   * Gets the page level settings (if they exist) for the active page
   * @returns
   */
  getPageSettings(): Params | undefined {
    const activePage = this.get();
    return activePage?.[PAGE_KEY];
  }

  updatePageSettings(updates: Params, updateCallback?: () => void) {
    const activePage = this.get();
    if (!activePage) {
      return;
    }
    if (Object.keys(updates).length === 0) {
      return;
    }
    const existingSettings = activePage[PAGE_KEY] || {};
    const newSettings = {
      ...{},
      ...existingSettings,
      ...updates,
    };
    activePage[PAGE_KEY] = newSettings;
    if (deepEqual(existingSettings, newSettings)) {
      return;
    }
    updateCallback?.();
    PreferenceClient.getInstance().store(`${this.active}${CACHE_SUFFIX}`, activePage);
  }

  /**
   * Gets the settings for a component on the active page
   * @param name
   * @returns
   */
  getComponentSettings(name: string): Params | undefined {
    const activePage = this.get();
    return activePage?.[name];
  }

  updateComponentSettings(name: string, updates: Params, updateCallback: () => void) {
    const activePage = this.get();
    if (!activePage) {
      console.warn('no active page');
      return;
    }

    const existingSettings = activePage[name] || {};
    const newSettings = {
      ...{},
      ...existingSettings,
      ...updates,
    };
    const toDelete = Object.keys(newSettings).filter(key => newSettings[key] === null);
    toDelete.forEach(key => {
      delete newSettings[key];
    });
    activePage[name] = newSettings;

    if (deepEqual(existingSettings, newSettings)) {
      return;
    }
    updateCallback();
    PreferenceClient.getInstance().store(`${this.active}${CACHE_SUFFIX}`, activePage);
    return true;
  }

  /**
   * Gets all settings (page level and all components) for the active page
   * @returns
   */
  get(): PageSettings | undefined {
    return this.pages[this.active];
  }

  async set(name: string, newPageSettings: PageSettings) {
    this.active = name;
    this.pages[name] = { ...newPageSettings };
    await PreferenceClient.getInstance().store(`${name}${CACHE_SUFFIX}`, this.pages[name]);
  }

  preCacheSettings(settings: SettingsPayload) {
    sessionStorage.setItem(PAGE_SETTINGS_PRE_CACHE, JSON.stringify(settings));
  }

  getPreCachedSettings(): PageSettings | undefined {
    const preCachedStr = sessionStorage.getItem(PAGE_SETTINGS_PRE_CACHE);
    sessionStorage.removeItem(PAGE_SETTINGS_PRE_CACHE);
    if (!preCachedStr) {
      return;
    }

    const preCached = JSON.parse(preCachedStr) as SettingsPayload;
    let cachedSettings: PageSettings = {};
    if (preCached.page) {
      cachedSettings[PAGE_KEY] = preCached.page;
    }
    if (preCached.components) {
      cachedSettings = {
        ...cachedSettings,
        ...preCached.components,
      };
    }
    return cachedSettings;
  }

  /**
   * Validates customFeatureTiers format for segmentation page and clears cache if invalid.
   * Detects old format (without menu prefix) and returns empty settings to force reset.
   * @param pageSettings - The cached page settings for segmentation
   * @returns Valid page settings or empty settings if invalid format detected
   */
  private validateAndClearInvalidCustomFeatures(pageSettings: PageSettings): PageSettings {
    const menuKeys = ['grouping', 'sgSegment'];

    for (const componentName of Object.keys(pageSettings)) {
      const componentSettings = pageSettings[componentName];

      if (componentSettings?.customFeatureTiers && Array.isArray(componentSettings.customFeatureTiers)) {
        const tiers = componentSettings.customFeatureTiers;

        const hasInvalidTier = tiers.some(tier => {
          if (!tier || typeof tier !== 'string') return true;
          if (tier.startsWith('null:')) return true;

          const hasValidPrefix = menuKeys.some(menu => tier.startsWith(`${menu}:`));
          return tier.startsWith('name:') && !hasValidPrefix;
        });

        if (hasInvalidTier) {
          return { [PAGE_KEY]: {} };
        }
      }
    }

    return pageSettings;
  }

  static getInstance(): PageSettingsManager {
    if (!PageSettingsManager._instance) {
      PageSettingsManager._instance = new PageSettingsManager();
    }
    return PageSettingsManager._instance;
  }
}
